import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await api.post("/auth/password-reset/request", { email });
            setDone(true);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || "Request failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-bone flex items-center justify-center px-4 py-12" data-testid="forgot-password-page">
            <div className="w-full max-w-md">
                <Link to="/login" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-ink/60 hover:text-klein" data-testid="forgot-back-link">
                    <ArrowLeft className="w-3 h-3" /> Back to sign in
                </Link>
                <div className="mt-8 bg-white border border-ink/10 p-10">
                    <div className="overline">Account recovery</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-3xl text-ink leading-tight">
                        Forgot your password?
                    </h1>
                    {done ? (
                        <div className="mt-8 space-y-4" data-testid="forgot-success">
                            <div className="inline-flex items-center justify-center w-12 h-12 bg-green-50 text-green-700">
                                <CheckCircle2 className="w-6 h-6" />
                            </div>
                            <p className="text-sm text-ink/70 leading-relaxed">
                                If an account exists for <span className="font-semibold text-ink">{email}</span>,
                                a password reset link has been emailed. Check your inbox (and spam folder) — the link is valid for one hour.
                            </p>
                            <Link to="/login" className="inline-block mt-4 text-sm text-klein hover:underline">
                                Return to sign in →
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={onSubmit}>
                            <p className="mt-3 text-sm text-ink/60 leading-relaxed">
                                Enter the email tied to your account and we'll send you a reset link.
                            </p>
                            <div className="mt-8">
                                <label className="overline">Email</label>
                                <div className="relative">
                                    <Mail className="w-4 h-4 absolute left-0 top-1/2 mt-1 -translate-y-1/2 text-ink/30" />
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@company.com"
                                        className="mt-2 w-full border-b border-ink/20 bg-transparent py-3 pl-7 pr-0 focus:border-klein focus:outline-none text-ink placeholder:text-ink/30"
                                        data-testid="forgot-email-input"
                                    />
                                </div>
                            </div>
                            {error && (
                                <div className="mt-6 border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" data-testid="forgot-error">
                                    {error}
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={submitting}
                                className="mt-8 w-full bg-klein text-white px-6 py-4 font-medium hover:bg-klein/90 disabled:opacity-60"
                                data-testid="forgot-submit-button"
                            >
                                {submitting ? "Sending…" : "Send reset link"}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
